import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, Button, useMediaQuery } from '@mui/material';
import { styled, useTheme, keyframes } from '@mui/material/styles';
import { ChevronDown } from 'lucide-react';
import { createProCheckoutSession } from '../AGTHall/services/AGTHallService';
import { useProStatus } from '../../context/ProContext';
import { useTranslation } from 'react-i18next';

// Import the new components
import AGTMainCategoriesSection from './AGTMainCategoriesSection';
import AGTProSection from './AGTProSection';
import AGTEduSection from './AGTEduSection';
import AGTUpsSection from './AGTUpsSection';
import AGTJobSection from './AGTJobSection';
import AGTFitSection from './AGTFitSection';
import AGTHealthSection from './AGTHealthSection';
import AGTLegalSection from './AGTLegalSection';
import AGTTranslateSection from './AGTTranslateSection';
import SlidingTestimonialCards from './SlidingTestimonialCards';
import { testimonialData } from './testimonialData';
import TestimonialCategorySelector from './TestimonialCategorySelector';

const ACCENT_COLOR = '#007BFE';
const HOVER_COLOR = '#0069d9';

// Keyframes para el efecto de destacado
const highlightKeyframes = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(0, 123, 254, 0.4); }
  70% { box-shadow: 0 0 0 10px rgba(0, 123, 254, 0); }
  100% { box-shadow: 0 0 0 0 rgba(0, 123, 254, 0); }
`;

// Keyframes para el indicador de desplazamiento
const bounceKeyframes = keyframes`
  0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
  40% { transform: translateY(-10px); }
  60% { transform: translateY(-5px); }
`;

// Título estático con display flex para alinear título y ayuda de compra
const StoreTitle = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(4),
  marginBottom: theme.spacing(6),
  display: 'flex',
  justifyContent: 'space-between', // Espacio entre el título y la ayuda
  alignItems: 'center',
  width: '100%',
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: theme.spacing(3),
  },
}));

// Estilo para el contenedor de ayuda de compra
const ShoppingHelpContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  marginRight: theme.spacing(4), // Added right margin to prevent overflow issues
  position: 'relative', // Added to ensure proper positioning
  zIndex: 3, // Added to ensure it stays above other elements
  [theme.breakpoints.down('sm')]: {
    marginRight: theme.spacing(2),
    flexDirection: 'row-reverse', // This reverses the order in mobile devices
  },
}));

// Estilo para el texto de ayuda
const HelpTextContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-end',
  [theme.breakpoints.down('sm')]: {
    alignItems: 'flex-start', // Align text to the left on mobile
  },
}));

// Estilo para el enlace de especialista
const SpecialistLink = styled('a')(({ theme }) => ({
  color: ACCENT_COLOR,
  textDecoration: 'none',
  fontWeight: 500,
  fontSize: '0.875rem',
  cursor: 'pointer', // Add cursor pointer to make it look clickable
  '&:hover': {
    textDecoration: 'underline',
  },
}));

// Estilo para la imagen de perfil
const ProfileImage = styled('img')(() => ({
  width: '40px',
  height: '40px',
  borderRadius: '50%',
  objectFit: 'cover',
  objectPosition: 'center 0%', // Ajustado para enfocar más arriba (30% desde arriba)
}));

// Wrapper para el contenido desplazable
const ScrollContainer = styled(Box)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    width: '100%',
    height: '100%',
    overflowY: 'auto',
    overflowX: 'hidden', // Importante: evita el scroll horizontal del contenedor principal
    position: 'relative',
    backgroundColor: isWallpaperMode ? 'transparent' : (theme.palette.mode === 'dark' ? '#000000' : '#FFFFFF'),
    scrollBehavior: 'smooth',
  };
});

// Contenedor principal con padding izquierdo de 140px - PRESERVANDO EL DISEÑO ORIGINAL EN DESKTOP
const MainContentContainer = styled(Box)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    padding: theme.spacing(3, 0),
    paddingLeft: '140px', // This is the key padding we need to maintain for desktop
    width: '100%',
    overflow: 'visible',
    position: 'relative', // Add position relative
    backgroundColor: isWallpaperMode ? 'transparent' : 'inherit',

    // Responsive adjustments only for mobile
    [theme.breakpoints.down('md')]: {
      paddingLeft: theme.spacing(3),
      paddingRight: theme.spacing(3),
    },
    [theme.breakpoints.down('sm')]: {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
    },
  };
});

// Nuevo componente para secciones centradas que contrarresta el padding izquierdo
const CenteredSection = styled(Box)(({ theme }) => ({
  marginLeft: '-140px',
  width: 'calc(100% + 140px)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  overflowX: 'visible', // Ensure overflow is visible
 
  [theme.breakpoints.down('md')]: {
    marginLeft: '0',
    width: '100%',
  },
}));

// Nuevo componente para el texto informativo de los testimonios
const TestimonialDisclaimer = styled(Typography)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    fontSize: '0.85rem',
    color: isWallpaperMode
      ? 'rgba(255, 255, 255, 0.7)'
      : (theme.palette.mode === 'dark' ? '#a3a3a3' : '#6e6e73'),
    textAlign: 'center',
    maxWidth: '800px',
    margin: '0 auto',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(6),
    padding: theme.spacing(0, 2),

    [theme.breakpoints.down('sm')]: {
      fontSize: '0.75rem',
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(4),
    },
  };
});

// Componentes para el selector de categorías de uso
const CategorySelector = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(8),
  marginBottom: theme.spacing(6),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center', // Center the content horizontally
  
  [theme.breakpoints.down('md')]: {
    marginTop: theme.spacing(7),
    marginBottom: theme.spacing(5),
  },
  [theme.breakpoints.down('sm')]: {
    marginTop: theme.spacing(6),
    marginBottom: theme.spacing(4),
  },
}));

const CategorySelectorContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-start',
  overflowX: 'auto',
  padding: theme.spacing(1, 0),
  marginBottom: theme.spacing(4),
  WebkitOverflowScrolling: 'touch',
  scrollbarWidth: 'none',
  msOverflowStyle: 'none',
  width: '100%',
 
  // Fix for mobile scrolling
  [theme.breakpoints.down('sm')]: {
    justifyContent: 'flex-start',
    paddingLeft: theme.spacing(0), // Reduce padding to avoid interference
    paddingRight: theme.spacing(0),
    position: 'relative',
    '&::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      right: 0,
      height: '100%',
      width: '30px',
      background: `linear-gradient(to right, transparent, ${theme.palette.background.default})`,
      pointerEvents: 'none', // Ensure this is set to allow interaction through the gradient
      zIndex: 1, // Lower z-index to prevent interaction issues
    }
  },
 
  '&::-webkit-scrollbar': {
    display: 'none',
  },
 
  // Add touch-action property to ensure proper touch handling
  touchAction: 'pan-x',
}));

const CategoryButton = styled(Button)(({ theme, selected }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    minWidth: '120px',
    height: '42px',
    marginRight: theme.spacing(1.5),
    borderRadius: '21px',
    padding: theme.spacing(0, 2),
    boxShadow: 'none',
    backgroundColor: selected
      ? (isWallpaperMode ? '#FFFFFF' : ACCENT_COLOR)
      : (isWallpaperMode ? 'rgba(128, 128, 128, 0.3)' : (theme.palette.mode === 'dark' ? '#1c1c1e' : '#f2f2f7')),
    backdropFilter: isWallpaperMode && !selected ? 'blur(10px)' : 'none',
    WebkitBackdropFilter: isWallpaperMode && !selected ? 'blur(10px)' : 'none',
    border: isWallpaperMode && !selected ? '1px solid rgba(128, 128, 128, 0.3)' : 'none',
    color: selected
      ? (isWallpaperMode ? '#000000' : '#ffffff')
      : (isWallpaperMode ? '#FFFFFF' : (theme.palette.mode === 'dark' ? '#ffffff' : '#000000')),
    textTransform: 'none',
    fontWeight: 500,
    fontSize: '0.875rem',
    whiteSpace: 'nowrap',
    transition: 'background-color 0.2s ease, transform 0.1s ease',
    '&:hover': {
      backgroundColor: selected
        ? (isWallpaperMode ? 'rgba(255, 255, 255, 0.85)' : HOVER_COLOR)
        : (isWallpaperMode ? 'rgba(128, 128, 128, 0.5)' : (theme.palette.mode === 'dark' ? '#2a2a2a' : '#f5f5f5')),
      transform: 'translateY(-2px)',
    },

    [theme.breakpoints.down('sm')]: {
      minWidth: '110px',
      height: '38px',
      fontSize: '0.8rem',
    },
  };
});

// Add a new styled component for the testimonial section title
const TestimonialSectionTitle = styled(Typography)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    textAlign: 'center',
    fontWeight: 600,
    fontSize: '42px',
    color: isWallpaperMode ? '#FFFFFF' : (theme.palette.mode === 'dark' ? '#ffffff' : '#000000'),
    margin: theme.spacing(6, 0, 4),
    [theme.breakpoints.down('md')]: {
      fontSize: '28px',
      margin: theme.spacing(5, 0, 3),
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: '24px',
      margin: theme.spacing(4, 0, 2),
    },
  };
});

// Create a styled component for the testimonial container with fade transitions
const TestimonialContainer = styled(Box)(({ theme, visible }) => ({
  opacity: visible ? 1 : 0,
  transition: 'opacity 0.5s ease-in-out',
  display: visible ? 'block' : 'none',
  marginBottom: theme.spacing(4),
  width: '100%', // Asegura que ocupe todo el ancho disponible
}));

// Indicador de desplazamiento animado
const ScrollIndicator = styled(Box)(({ theme, visible }) => ({
  position: 'fixed',
  bottom: '20px',
  right: '20px',
  backgroundColor: ACCENT_COLOR,
  color: '#fff',
  borderRadius: '50%',
  width: '40px',
  height: '40px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  boxShadow: '0 2px 10px rgba(0, 123, 254, 0.5)',
  opacity: visible ? 1 : 0,
  transform: visible ? 'scale(1)' : 'scale(0.8)',
  transition: 'all 0.3s ease',
  zIndex: 1000,
  animation: visible ? `${bounceKeyframes} 2s infinite` : 'none',
  pointerEvents: visible ? 'auto' : 'none',
  cursor: 'pointer',
  
  [theme.breakpoints.down('sm')]: {
    width: '36px',
    height: '36px',
  },
}));

const ProductSectionContainer = styled(Box)(({ theme, highlight }) => ({
  marginBottom: theme.spacing(6),
  paddingRight: theme.spacing(4), // Añadido padding derecho para compensar el contenedor principal
  animation: highlight ? `${highlightKeyframes} 1.5s ease-out` : 'none',
  overflow: 'visible', // Permitir el desbordamiento visual de las tarjetas
  
  [theme.breakpoints.down('md')]: {
    paddingRight: 0,
    marginBottom: theme.spacing(5),
  },
  [theme.breakpoints.down('sm')]: {
    marginBottom: theme.spacing(4),
  },
}));

const AGTPromo = ({ onOpenAGTUps, onOpenAGTEdu, onOpenAGTHealth, userId }) => {
  const theme = useTheme();
  const [isLoading, setIsLoading] = useState(false);
  const { isPro, checkStatus } = useProStatus();
  const { t } = useTranslation();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [highlightedSection, setHighlightedSection] = useState(null);
  const [showScrollIndicator, setShowScrollIndicator] = useState(false);
  const [selectedTestimonialProduct, setSelectedTestimonialProduct] = useState('all');
  const [highlightedTestimonialProduct, setHighlightedTestimonialProduct] = useState(null);
  const [isTestimonialTransitioning, setIsTestimonialTransitioning] = useState(false);
  
  // Media queries for responsive design
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  
  // Referencias para las secciones de productos
  const scrollContainerRef = useRef(null);

  // Funciones para manejar la apertura de modales para los nuevos productos
  const onOpenAGTJob = () => {
    console.log("Opening AGT Job modal");
    // Aquí implementarías la lógica para abrir el modal de AGT Job
    // Similar a onOpenAGTEdu o onOpenAGTUps
  };

  const onOpenAGTFit = () => {
    console.log("Opening AGT Fit modal");
    // Aquí implementarías la lógica para abrir el modal de AGT Fit
  };

  // La función ahora usa el prop onOpenAGTHealth que viene del componente padre
  const handleOpenAGTHealth = () => {
    console.log("Opening AGT Health");
    // Llamamos a la función proporcionada por el componente padre
    if (onOpenAGTHealth) {
      onOpenAGTHealth();
    }
    // Guardamos en localStorage que el usuario ha accedido a AGT Health
    localStorage.setItem('showHealthInNav', 'true');
  };

  const onOpenAGTLegal = () => {
    console.log("Opening AGT Legal modal");
    // Aquí implementarías la lógica para abrir el modal de AGT Legal
  };

  const onOpenAGTTranslate = () => {
    console.log("Opening AGT Translate modal");
    // Aquí implementarías la lógica para abrir el modal de AGT Translate
  };

  // Handle highlight changes from the TestimonialCategorySelector
  const handleTestimonialHighlightChange = (productId) => {
    if (isTestimonialTransitioning) return;
    
    setIsTestimonialTransitioning(true);
    
    // First fade out current highlighted product
    setHighlightedTestimonialProduct(null);
    
    // Then set the new one after a short delay
    setTimeout(() => {
      setHighlightedTestimonialProduct(productId);
      setIsTestimonialTransitioning(false);
    }, 300);
  };

  // Obtenemos los flags para los productos desde localStorage
  const hasAGTUps = localStorage.getItem('showUpsInNav') === 'true';
  const hasAGTJob = localStorage.getItem('showJobInNav') === 'true';
  const hasAGTFit = localStorage.getItem('showFitInNav') === 'true';
  const hasAGTHealth = localStorage.getItem('showHealthInNav') === 'true';
  const hasAGTLegal = localStorage.getItem('showLegalInNav') === 'true';
  const hasAGTTranslate = localStorage.getItem('showTranslateInNav') === 'true';

  // Initialize with a default highlighted product when in 'all' mode
  useEffect(() => {
    if (selectedTestimonialProduct === 'all' && !highlightedTestimonialProduct) {
      setHighlightedTestimonialProduct('pro');
    }
  }, [selectedTestimonialProduct, highlightedTestimonialProduct]);

  useEffect(() => {
    if (userId) {
      checkStatus(userId);
    }

    // Efecto para detectar el scroll
    const handleScroll = () => {
      const scrollContainer = scrollContainerRef.current;
      if (!scrollContainer) return;
      
      // Ocultar el indicador de scroll cuando estamos cerca del final
      const isNearBottom = scrollContainer.scrollTop + scrollContainer.clientHeight >= scrollContainer.scrollHeight - 100;
      if (isNearBottom) {
        setShowScrollIndicator(false);
      }
    };

    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll);
    }
    
    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener('scroll', handleScroll);
      }
    };
  }, [userId, checkStatus]);

  const handleProCheckout = async () => {
    if (isPro) return;
    
    try {
      setIsLoading(true);
      const checkoutUrl = await createProCheckoutSession(userId);
      if (checkoutUrl) {
        window.location.href = checkoutUrl;
      }
    } catch (error) {
      console.error('Error creating checkout session:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Función auxiliar para comprobar si un producto está visible en la categoría actual
  const isProductVisibleInCurrentCategory = (productId) => {
    if (selectedCategory === 'all') return true;
    
    switch (productId) {
      case 1: // AGT Pro
        return selectedCategory === 'personal';
      case 2: // AGT Edu
        return selectedCategory === 'personal' || selectedCategory === 'organizations';
      case 3: // AGT Ups
        return selectedCategory === 'business';
      case 4: // AGT Job
        return selectedCategory === 'personal' || selectedCategory === 'business';
      case 5: // AGT Fit
        return selectedCategory === 'personal';
      case 6: // AGT Health
        return selectedCategory === 'personal' || selectedCategory === 'organizations';
      case 7: // AGT Legal
        return selectedCategory === 'personal' || selectedCategory === 'business' || selectedCategory === 'organizations';
      case 8: // AGT Translate
        return selectedCategory === 'personal';
      default:
        return false;
    }
  };

  // Función para desplazarse a una sección específica con manejo mejorado
  const scrollToSection = (productId) => {
    if (productId === 0) { // AGT Full
      console.log("Learn more about AGT Full");
      return; // Early return since we don't have a section to scroll to yet
    }
    
    const section = productId === 1 ? 'pro' : 
                   productId === 2 ? 'edu' : 
                   productId === 3 ? 'ups' :
                   productId === 4 ? 'job' :
                   productId === 5 ? 'fit' :
                   productId === 6 ? 'health' :
                   productId === 7 ? 'legal' : 'translate';
    
    // Determinamos la categoría apropiada para este producto
    let targetCategory = 'all';
    switch (productId) {
      case 1: // AGT Pro
        targetCategory = 'personal';
        break;
      case 2: // AGT Edu
        targetCategory = 'personal'; // Elegimos personal como prioridad
        break;
      case 3: // AGT Ups
        targetCategory = 'business';
        break;
      case 4: // AGT Job
        targetCategory = 'personal'; // Elegimos personal como prioridad
        break;
      case 5: // AGT Fit
        targetCategory = 'personal';
        break;
      case 6: // AGT Health
        targetCategory = 'personal'; // Elegimos personal como prioridad
        break;
      case 7: // AGT Legal
        targetCategory = 'personal'; // Elegimos personal como prioridad
        break;
      case 8: // AGT Translate
        targetCategory = 'personal';
        break;
      default:
        targetCategory = 'all';
    }
    
    // Verificar si el producto está visible en la categoría actual
    const isVisible = isProductVisibleInCurrentCategory(productId);
    
    // Si necesitamos cambiar de categoría
    if (!isVisible) {
      // Cambiamos la categoría primero
      setSelectedCategory(targetCategory);
      
      // Usamos un temporizador para permitir que React actualice el DOM
      setTimeout(() => {
        attemptScrollToElement(`product-section-${section}`, 0);
      }, 100);
    } else {
      // Si ya es visible, intentamos hacer scroll inmediatamente
      attemptScrollToElement(`product-section-${section}`, 0);
    }
  };
  
  // Función para intentar hacer scroll a un elemento con reintentos
  const attemptScrollToElement = (elementId, attempts) => {
    const maxAttempts = 5;
    const element = document.getElementById(elementId);
    
    if (element && scrollContainerRef.current) {
      // Mostrar el indicador de desplazamiento
      setShowScrollIndicator(true);
      
      // Hacer scroll al elemento
      scrollToElement(element);
    } else if (attempts < maxAttempts) {
      // Si el elemento no existe y no hemos superado el máximo de intentos, reintentamos
      console.log(`Element ${elementId} not found, retrying... (attempt ${attempts + 1})`);
      setTimeout(() => {
        attemptScrollToElement(elementId, attempts + 1);
      }, 200); // Incrementamos el tiempo entre intentos
    } else {
      console.error(`Failed to find element ${elementId} after ${maxAttempts} attempts`);
    }
  };
  
  // Función auxiliar para hacer scroll a un elemento
  const scrollToElement = (element) => {
    const scrollContainer = scrollContainerRef.current;
    
    // Calcular la posición del elemento
    const elementRect = element.getBoundingClientRect();
    const containerRect = scrollContainer.getBoundingClientRect();
    const relativeTop = elementRect.top - containerRect.top + scrollContainer.scrollTop;
    
    // Hacer scroll con animación
    scrollContainer.scrollTo({
      top: relativeTop - 20, // Pequeño offset para mejor visualización
      behavior: 'smooth'
    });
    
    // Destacar la sección
    setHighlightedSection(element.id);
    setTimeout(() => {
      setHighlightedSection(null);
    }, 1500);
    
    // Ocultar el indicador después de un tiempo
    setTimeout(() => {
      setShowScrollIndicator(false);
    }, 2000);
  };

  // Función para manejar el cambio de categoría
  const handleCategoryChange = (categoryId) => {
    setSelectedCategory(categoryId);
  };

  // Manejador para la suscripción a notificaciones
  const handleNotificationSubscribe = () => {
    // Aquí iría la lógica para conectar con SES o el servicio de email que uses
    console.log("Notification subscription requested");
    // También podrías mostrar un mensaje de confirmación
  };

  // Datos para las categorías principales
  const mainCategories = [
    {
      id: 0,
      title: t('agtStore.products.agtFull.title'),
      image: "https://agtupsproductsimages.s3.us-east-2.amazonaws.com/gallery/AGT+Full+v2+(Store).png",
      price: t('agtStore.products.agtFull.price')
    },
    {
      id: 1,
      title: t('agtStore.products.agtPro.title'),
      image: "https://agtupsproductsimages.s3.us-east-2.amazonaws.com/gallery/AGT+Pro+(Store).png",
      price: t('agtStore.products.agtPro.price')
    },
    {
      id: 2,
      title: t('agtStore.products.agtEdu.title'),
      image: "https://agtupsproductsimages.s3.us-east-2.amazonaws.com/gallery/AGT+Edu+(Store).png",
      price: t('agtStore.products.agtEdu.price')
    },
    {
      id: 3,
      title: t('agtStore.products.agtUps.title'),
      image: "https://agtupsproductsimages.s3.us-east-2.amazonaws.com/gallery/AGT+Ups+v2+(Store).png",
      price: t('agtStore.products.agtUps.price')
    },
    // Nuevos productos añadidos en el orden acordado
    {
      id: 4,
      title: t('agtStore.products.agtJob.title'),
      image: "https://agtupsproductsimages.s3.us-east-2.amazonaws.com/gallery/AGT+Job+(Store).png", // URL de placeholder
      price: t('agtStore.products.agtJob.price')
    },
    {
      id: 5,
      title: t('agtStore.products.agtFit.title'),
      image: "https://agtupsproductsimages.s3.us-east-2.amazonaws.com/gallery/AGT+Fit+(Store).png", // URL de placeholder
      price: t('agtStore.products.agtFit.price')
    },
    {
      id: 6,
      title: t('agtStore.products.agtHealth.title'),
      image: "https://agtupsproductsimages.s3.us-east-2.amazonaws.com/gallery/AGT+Health+v2+(Store)", // URL de placeholder
      price: t('agtStore.products.agtHealth.price')
    },
    {
      id: 7,
      title: t('agtStore.products.agtLegal.title'),
      image: "https://agtupsproductsimages.s3.us-east-2.amazonaws.com/gallery/AGT+Legal+(Store).png", // URL de placeholder
      price: t('agtStore.products.agtLegal.price')
    },
    {
      id: 8,
      title: t('agtStore.products.agtTranslate.title'),
      image: "https://agtupsproductsimages.s3.us-east-2.amazonaws.com/gallery/AGT+Translate+(Store).png", // URL de placeholder
      price: t('agtStore.products.agtTranslate.price')
    }
  ];

  // Categorías de uso para el selector
  const useCategories = [
    { id: 'all', title: t('agtStore.mainCategories.all') },
    { id: 'personal', title: t('agtStore.mainCategories.personal') },
    { id: 'business', title: t('agtStore.mainCategories.business') },
    { id: 'organizations', title: t('agtStore.mainCategories.organizations') },
  ];

  return (
    <ScrollContainer className="scroll-container" ref={scrollContainerRef}>
      {/* Contenido principal con padding izquierdo de 140px */}
      <MainContentContainer>
        {/* Título estático inicial con ayuda de compra */}
        <StoreTitle>
          {/* Lado izquierdo - Título */}
          <Box>
            <Box sx={{ 
              display: 'flex', 
              flexWrap: 'wrap',
              flexDirection: isMobile ? 'column' : 'row'
            }}>
              <Typography
                variant="h1"
                sx={{
                  fontWeight: 600,
                  fontSize: isMobile ? '32px' : (isTablet ? '36px' : '42px'),
                  color: theme.palette.actualMode === 'wallpaper'
                    ? '#FFFFFF'
                    : (theme.palette.mode === 'dark' ? '#ffffff' : '#000000'),
                  marginRight: isMobile ? '0' : '12px'
                }}
              >
                {t('agtStore.title')}
              </Typography>
              <Typography
                variant="h1"
                sx={{
                  fontWeight: 600,
                  fontSize: isMobile ? '32px' : (isTablet ? '36px' : '42px'),
                  color: theme.palette.actualMode === 'wallpaper'
                    ? 'rgba(255, 255, 255, 0.85)'
                    : '#6e6e73',
                }}
              >
                {t('agtStore.subtitle')}
              </Typography>
            </Box>
            <Typography
              variant="h1"
              sx={{
                fontWeight: 600,
                fontSize: isMobile ? '32px' : (isTablet ? '36px' : '42px'),
                color: theme.palette.actualMode === 'wallpaper'
                  ? 'rgba(255, 255, 255, 0.85)'
                  : '#6e6e73',
                display: 'block',
                lineHeight: 1.2
              }}
            >
              {t('agtStore.subtitleEnd')}
            </Typography>
          </Box>
          
          {/* Lado derecho - Ayuda de compra */}
          <ShoppingHelpContainer>
            <HelpTextContainer>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.actualMode === 'wallpaper'
                    ? 'rgba(255, 255, 255, 0.85)'
                    : (theme.palette.mode === 'dark' ? '#a3a3a3' : '#666666'),
                  fontSize: '0.875rem',
                }}
              >
                {t('agtStore.needHelp')}
              </Typography>
              <SpecialistLink
                onClick={() => {
                  if (window.Intercom) {
                    window.Intercom('show');
                  } else {
                    console.warn('Intercom is not available');
                  }
                }}
                sx={{
                  color: theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : ACCENT_COLOR,
                }}
              >
                {t('agtStore.askSpecialist')}
              </SpecialistLink>
            </HelpTextContainer>
            <ProfileImage 
              src="https://agtupsproductsimages.s3.us-east-2.amazonaws.com/gallery/AGT+Support+(Profile+Picture).png" 
              alt="Support Specialist"
            />
          </ShoppingHelpContainer>
        </StoreTitle>
        
        {/* Main Categories Section - Extracted Component */}
        <AGTMainCategoriesSection 
          mainCategories={mainCategories}
          isPro={isPro}
          hasAGTUps={hasAGTUps}
          hasAGTJob={hasAGTJob}
          hasAGTFit={hasAGTFit}
          hasAGTHealth={hasAGTHealth}
          hasAGTLegal={hasAGTLegal}
          hasAGTTranslate={hasAGTTranslate}
          handleProCheckout={handleProCheckout}
          onOpenAGTEdu={onOpenAGTEdu}
          onOpenAGTUps={onOpenAGTUps}
          onOpenAGTJob={onOpenAGTJob}
          onOpenAGTFit={onOpenAGTFit}
          onOpenAGTHealth={handleOpenAGTHealth} // Actualizado: Ahora usa handleOpenAGTHealth
          onOpenAGTLegal={onOpenAGTLegal}
          onOpenAGTTranslate={onOpenAGTTranslate}
          scrollToSection={scrollToSection}
          isMobile={isMobile}
          isTablet={isTablet}
        />
        
        {/* Selector de Categorías de Uso - ACTUALIZADO: Ahora correctamente centrado */}
        <CenteredSection>
          <CategorySelector>
            <CategorySelectorContainer>
              {useCategories.map((category) => (
                <CategoryButton 
                  key={category.id}
                  selected={selectedCategory === category.id}
                  onClick={() => handleCategoryChange(category.id)}
                >
                  {category.title}
                </CategoryButton>
              ))}
            </CategorySelectorContainer>
          </CategorySelector>
        </CenteredSection>

        {/* AGT Pro - mostrar en Personal */}
        {(selectedCategory === 'all' || selectedCategory === 'personal') && (
          <ProductSectionContainer 
            id="product-section-pro" 
            highlight={highlightedSection === 'product-section-pro'}
          >
            <AGTProSection 
              isPro={isPro}
              handleProCheckout={handleProCheckout}
              isLoading={isLoading}
              t={t}
              isMobile={isMobile}
              isTablet={isTablet}
            />
          </ProductSectionContainer>
        )}
        
        {/* AGT Edu - mostrar en Personal y Organizations */}
        {(selectedCategory === 'all' || selectedCategory === 'personal' || selectedCategory === 'organizations') && (
          <ProductSectionContainer 
            id="product-section-edu" 
            highlight={highlightedSection === 'product-section-edu'}
          >
            <AGTEduSection 
              onOpenAGTEdu={onOpenAGTEdu}
              t={t}
              isMobile={isMobile}
              isTablet={isTablet}
            />
          </ProductSectionContainer>
        )}
        
        {/* AGT Ups - mostrar en Business */}
        {(selectedCategory === 'all' || selectedCategory === 'business') && (
          <ProductSectionContainer 
            id="product-section-ups" 
            highlight={highlightedSection === 'product-section-ups'}
          >
            <AGTUpsSection 
              onOpenAGTUps={onOpenAGTUps}
              hasAGTUps={hasAGTUps}
              t={t}
              isMobile={isMobile}
              isTablet={isTablet}
            />
          </ProductSectionContainer>
        )}

        {/* AGT Job - mostrar en Personal y Business */}
        {(selectedCategory === 'all' || selectedCategory === 'personal' || selectedCategory === 'business') && (
          <ProductSectionContainer 
            id="product-section-job" 
            highlight={highlightedSection === 'product-section-job'}
          >
            <AGTJobSection 
              onOpenAGTJob={onOpenAGTJob}
              hasAGTJob={hasAGTJob}
              t={t}
              isMobile={isMobile}
              isTablet={isTablet}
            />
          </ProductSectionContainer>
        )}

        {/* AGT Fit - mostrar en Personal */}
        {(selectedCategory === 'all' || selectedCategory === 'personal') && (
          <ProductSectionContainer 
            id="product-section-fit" 
            highlight={highlightedSection === 'product-section-fit'}
          >
            <AGTFitSection 
              onOpenAGTFit={onOpenAGTFit}
              hasAGTFit={hasAGTFit}
              t={t}
              isMobile={isMobile}
              isTablet={isTablet}
            />
          </ProductSectionContainer>
        )}

        {/* AGT Health - mostrar en Personal y Organizations */}
        {(selectedCategory === 'all' || selectedCategory === 'personal' || selectedCategory === 'organizations') && (
          <ProductSectionContainer 
            id="product-section-health" 
            highlight={highlightedSection === 'product-section-health'}
          >
            <AGTHealthSection 
              onOpenAGTHealth={handleOpenAGTHealth} // Actualizado: Ahora usa handleOpenAGTHealth
              hasAGTHealth={hasAGTHealth}
              t={t}
              isMobile={isMobile}
              isTablet={isTablet}
            />
          </ProductSectionContainer>
        )}

        {/* AGT Legal - mostrar en Personal, Business y Organizations */}
        {(selectedCategory === 'all' || selectedCategory === 'personal' || selectedCategory === 'business' || selectedCategory === 'organizations') && (
          <ProductSectionContainer 
            id="product-section-legal" 
            highlight={highlightedSection === 'product-section-legal'}
          >
            <AGTLegalSection 
              onOpenAGTLegal={onOpenAGTLegal}
              hasAGTLegal={hasAGTLegal}
              t={t}
              isMobile={isMobile}
              isTablet={isTablet}
            />
          </ProductSectionContainer>
        )}

        {/* AGT Translate - mostrar en Personal */}
        {(selectedCategory === 'all' || selectedCategory === 'personal') && (
          <ProductSectionContainer 
            id="product-section-translate" 
            highlight={highlightedSection === 'product-section-translate'}
          >
            <AGTTranslateSection 
              onOpenAGTTranslate={onOpenAGTTranslate}
              hasAGTTranslate={hasAGTTranslate}
              t={t}
              isMobile={isMobile}
              isTablet={isTablet}
            />
          </ProductSectionContainer>
        )}

        {/* Testimonial Section Title - NUEVO y correctamente centrado */}
        {(selectedCategory === 'all' || selectedCategory === 'personal' || 
          selectedCategory === 'business' || selectedCategory === 'organizations') && (
          <CenteredSection>
            <TestimonialSectionTitle>
              {t('agtStore.testimonials.title')}
            </TestimonialSectionTitle>
            
            {/* Add the new TestimonialCategorySelector here with onHighlightChange prop */}
            <TestimonialCategorySelector 
              selectedProduct={selectedTestimonialProduct}
              onProductSelect={setSelectedTestimonialProduct}
              onHighlightChange={handleTestimonialHighlightChange}
              isMobile={isMobile}
              isTablet={isTablet}
              t={t}
            />
          </CenteredSection>
        )}

        {/* Testimonios deslizantes para cada sección, filtrados por producto seleccionado y correctamente centrados */}
        {(selectedCategory === 'all' || selectedCategory === 'personal') && (
          <CenteredSection>
            <TestimonialContainer 
              visible={(selectedTestimonialProduct === 'all' ? 
                (highlightedTestimonialProduct === 'pro' || (!highlightedTestimonialProduct && selectedTestimonialProduct === 'all')) : 
                selectedTestimonialProduct === 'pro')}
            >
              <SlidingTestimonialCards 
                testimonials={testimonialData} 
                sectionId="pro" 
              />
            </TestimonialContainer>
          </CenteredSection>
        )}

        {(selectedCategory === 'all' || selectedCategory === 'personal' || selectedCategory === 'organizations') && (
          <CenteredSection>
            <TestimonialContainer 
              visible={(selectedTestimonialProduct === 'all' ? 
                highlightedTestimonialProduct === 'edu' : 
                selectedTestimonialProduct === 'edu')}
            >
              <SlidingTestimonialCards 
                testimonials={testimonialData} 
                sectionId="edu" 
              />
            </TestimonialContainer>
          </CenteredSection>
        )}

        {(selectedCategory === 'all' || selectedCategory === 'business') && (
          <CenteredSection>
            <TestimonialContainer 
              visible={(selectedTestimonialProduct === 'all' ? 
                highlightedTestimonialProduct === 'ups' : 
                selectedTestimonialProduct === 'ups')}
            >
              <SlidingTestimonialCards 
                testimonials={testimonialData} 
                sectionId="ups" 
              />
            </TestimonialContainer>
          </CenteredSection>
        )}

        {(selectedCategory === 'all' || selectedCategory === 'personal' || selectedCategory === 'business') && (
          <CenteredSection>
            <TestimonialContainer 
              visible={(selectedTestimonialProduct === 'all' ? 
                highlightedTestimonialProduct === 'job' : 
                selectedTestimonialProduct === 'job')}
            >
              <SlidingTestimonialCards 
                testimonials={testimonialData} 
                sectionId="job" 
              />
            </TestimonialContainer>
          </CenteredSection>
        )}

        {(selectedCategory === 'all' || selectedCategory === 'personal') && (
          <CenteredSection>
            <TestimonialContainer 
              visible={(selectedTestimonialProduct === 'all' ? 
                highlightedTestimonialProduct === 'fit' : 
                selectedTestimonialProduct === 'fit')}
            >
              <SlidingTestimonialCards 
                testimonials={testimonialData} 
                sectionId="fit" 
              />
            </TestimonialContainer>
          </CenteredSection>
        )}

        {(selectedCategory === 'all' || selectedCategory === 'personal' || selectedCategory === 'organizations') && (
          <CenteredSection>
            <TestimonialContainer 
              visible={(selectedTestimonialProduct === 'all' ? 
                highlightedTestimonialProduct === 'health' : 
                selectedTestimonialProduct === 'health')}
            >
              <SlidingTestimonialCards 
                testimonials={testimonialData} 
                sectionId="health" 
              />
            </TestimonialContainer>
          </CenteredSection>
        )}

        {(selectedCategory === 'all' || selectedCategory === 'personal' || selectedCategory === 'business' || selectedCategory === 'organizations') && (
          <CenteredSection>
            <TestimonialContainer 
              visible={(selectedTestimonialProduct === 'all' ? 
                highlightedTestimonialProduct === 'legal' : 
                selectedTestimonialProduct === 'legal')}
            >
              <SlidingTestimonialCards 
                testimonials={testimonialData} 
                sectionId="legal" 
              />
            </TestimonialContainer>
          </CenteredSection>
        )}

        {(selectedCategory === 'all' || selectedCategory === 'personal') && (
          <CenteredSection>
            <TestimonialContainer 
              visible={(selectedTestimonialProduct === 'all' ? 
                highlightedTestimonialProduct === 'translate' : 
                selectedTestimonialProduct === 'translate')}
            >
              <SlidingTestimonialCards 
                testimonials={testimonialData} 
                sectionId="translate" 
              />
            </TestimonialContainer>
          </CenteredSection>
        )}

        {/* Texto informativo sobre los testimonios - NUEVO */}
        {(selectedCategory === 'all' || selectedCategory === 'personal' || 
          selectedCategory === 'business' || selectedCategory === 'organizations') && (
          <CenteredSection>
            <TestimonialDisclaimer>
              {t('agtStore.testimonials.disclaimer')}
            </TestimonialDisclaimer>
          </CenteredSection>
        )}

        {/* Nueva Sección de Suscripción - Estilo Apple */}
        <CenteredSection>
          <Box
            sx={{
              width: '100%',
              maxWidth: '900px',
              borderRadius: '12px',
              overflow: 'hidden',
              position: 'relative',
              marginTop: theme.spacing(4),
              marginBottom: theme.spacing(6),
              padding: theme.spacing(6, 4),
              background: theme.palette.actualMode === 'wallpaper'
                ? 'rgba(128, 128, 128, 0.3)'
                : (theme.palette.mode === 'dark'
                  ? 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)'
                  : 'linear-gradient(135deg, #f5f5f7 0%, #e5e5e5 100%)'),
              backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
              WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
              border: theme.palette.actualMode === 'wallpaper' ? '1px solid rgba(128, 128, 128, 0.3)' : 'none',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontWeight: 600,
                fontSize: isMobile ? '28px' : '36px',
                color: theme.palette.actualMode === 'wallpaper'
                  ? '#FFFFFF'
                  : (theme.palette.mode === 'dark' ? '#ffffff' : '#1d1d1f'),
                marginBottom: theme.spacing(1.5),
                letterSpacing: '-0.003em',
              }}
            >
              {t('agtStore.needMore.title')}
            </Typography>

            <Typography
              variant="body1"
              sx={{
                fontSize: isMobile ? '17px' : '19px',
                lineHeight: 1.47059,
                color: theme.palette.actualMode === 'wallpaper'
                  ? 'rgba(255, 255, 255, 0.85)'
                  : (theme.palette.mode === 'dark' ? '#a1a1a6' : '#6e6e73'),
                maxWidth: '600px',
                marginBottom: theme.spacing(3.5),
                letterSpacing: '-0.022em',
              }}
            >
              {t('agtStore.needMore.description')}
            </Typography>
            
            <Button
              variant="contained"
              onClick={handleNotificationSubscribe}
              sx={{
                backgroundColor: theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : ACCENT_COLOR,
                color: theme.palette.actualMode === 'wallpaper' ? '#000000' : '#ffffff',
                '&:hover': {
                  backgroundColor: theme.palette.actualMode === 'wallpaper'
                    ? 'rgba(255, 255, 255, 0.85)'
                    : HOVER_COLOR
                },
                borderRadius: '980px',
                textTransform: 'none',
                fontWeight: 600,
                padding: '12px 22px',
                fontSize: '17px',
                letterSpacing: '-0.022em',
                minWidth: '120px',
              }}
            >
              {t('agtStore.needMore.button')}
            </Button>

            <Typography
              variant="caption"
              sx={{
                fontSize: '12px',
                color: theme.palette.actualMode === 'wallpaper'
                  ? 'rgba(255, 255, 255, 0.7)'
                  : (theme.palette.mode === 'dark' ? '#a1a1a6' : '#86868b'),
                maxWidth: '500px',
                marginTop: theme.spacing(2.5),
                letterSpacing: '-0.016em',
              }}
            >
              {t('agtStore.needMore.disclaimer')}
            </Typography>
          </Box>
        </CenteredSection>
      </MainContentContainer>
      
      {/* Indicador de desplazamiento */}
      <ScrollIndicator 
        visible={showScrollIndicator}
        onClick={() => setShowScrollIndicator(false)}
        aria-label={t('agtStore.scrollIndicator')}
      >
        <ChevronDown size={isMobile ? 20 : 24} />
      </ScrollIndicator>
    </ScrollContainer>
  );
};

export default AGTPromo;